import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { rupee } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Cart() {
  const { cart, update, remove } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  if (cart.items.length === 0) {
    return (
      <div className="pt-32 pb-24 min-h-screen">
        <div className="max-w-md mx-auto px-5 text-center">
          <div className="w-16 h-16 rounded-full bg-surface grid place-items-center mx-auto mb-6"><ShoppingBag className="w-7 h-7 text-clay" /></div>
          <h1 className="font-serif text-4xl tracking-tight">Your basket is empty</h1>
          <p className="text-mute mt-3">Fill it with something made by real hands.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 rounded-full bg-clay text-sand px-7 py-3.5 mt-8 text-sm hover:bg-forest transition-colors" data-testid="empty-cart-shop-btn">
            Browse gifts <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 sm:pt-28 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="overline text-clay mb-3">Your basket</div>
        <h1 className="font-serif text-4xl sm:text-6xl tracking-tighter">{cart.items.length} {cart.items.length === 1 ? "item" : "items"}</h1>

        <div className="grid lg:grid-cols-[1fr_360px] gap-12 mt-10">
          <div className="space-y-6">
            {cart.items.map((it) => (
              <div key={it.product_id} className="flex gap-4 sm:gap-6 border-b border-line pb-6" data-testid={`cart-item-${it.product_id}`}>
                <Link to={`/product/${it.product_id}`} className="w-24 h-28 sm:w-28 sm:h-32 rounded-sm overflow-hidden bg-surface shrink-0">
                  <img src={it.image} alt={it.title} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="overline text-mute">{it.seller_name}</div>
                  <Link to={`/product/${it.product_id}`}><h3 className="font-serif text-lg sm:text-xl leading-snug mt-0.5">{it.title}</h3></Link>
                  <div className="text-sm text-mute mt-1">Delivers in {it.delivery_days} days</div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-line rounded-full">
                      <button onClick={() => update(it.product_id, it.qty - 1)} className="p-2" data-testid={`cart-minus-${it.product_id}`}><Minus className="w-4 h-4" /></button>
                      <span className="w-7 text-center text-sm">{it.qty}</span>
                      <button onClick={() => update(it.product_id, it.qty + 1)} className="p-2" data-testid={`cart-plus-${it.product_id}`}><Plus className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-semibold">{rupee(it.price * it.qty)}</span>
                      <button onClick={() => remove(it.product_id)} className="text-mute hover:text-clay" data-testid={`cart-remove-${it.product_id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:sticky lg:top-24 h-fit bg-surface rounded-sm p-6 border border-line">
            <h2 className="font-serif text-2xl tracking-tight">Order summary</h2>
            <div className="flex justify-between text-sm mt-5"><span className="text-mute">Subtotal</span><span>{rupee(cart.total)}</span></div>
            <div className="flex justify-between text-sm mt-2"><span className="text-mute">Delivery</span><span>Set by each maker</span></div>
            <div className="border-t border-line mt-4 pt-4 flex justify-between font-semibold text-lg"><span>Total</span><span data-testid="cart-total">{rupee(cart.total)}</span></div>
            <p className="text-xs text-mute mt-3">Full prepaid · No cash on delivery. Orders split per seller.</p>
            <button
              onClick={() => nav(user ? "/checkout" : "/login")}
              className="w-full rounded-full bg-clay text-sand py-3.5 mt-5 text-sm hover:bg-forest transition-colors"
              data-testid="checkout-btn"
            >
              {user ? "Proceed to checkout" : "Sign in to checkout"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
